<script setup lang="ts">
import type {Populated} from '~/types/populated'
import type {Project, Media} from '~/types/legacy'

type ProjectPreview = Omit<Populated<Project>, 'industries'> & {
  industries: string[]
  industriesSlugs: string[]
  countryName?: string
  image: Populated<Media>
}

const props = defineProps<{
  project: ProjectPreview
  industry?: string[]
}>()

const localePath = useLocalePath()

const path = computed(() => {
  if (props.industry) {
    return `/projects/${props.industry[0]}/${props.project.slug}`
  }

  return `/projects/${props.project.slug}`
})
</script>

<template>
  <article class="project px-2">
    <div class="project-content md:mt-0">
      <div class="project-image">
        <div class="relative aspect-video rounded-lg bg-white p-2 bordershadow-medium">
          <NuxtImg
            v-if="project.image"
            v-bind="resolveMediaProps(project.image)"
            :width="600"
            :height="400"
            class="h-full w-full rounded object-cover bordershadow-gray"
            fit="cover"
          />

          <div
            v-if="project.countryName"
            :title="project.countryName"
            class="absolute right-4 top-4 rounded-full bg-white px-2 py-1 text-sm shadow-sm"
          >
            {{ project.countryName }}
          </div>
        </div>
      </div>

      <div class="mt-6">
        <p class="text-gray-500">
          {{ project.industries.join(', ') }}
        </p>

        <Heading size="medium" class="mb-4 text-main-950">
          {{ props.project.title }}
        </Heading>

        <Paragraph size="normal" class="text-gray-700">
          {{ props.project.description }}
        </Paragraph>

        <nuxt-link :to="localePath(path)" class="group flex items-center py-3 font-bold text-main-600">
          {{ $t('common.see_how_it_works') }}
          <Icon name="lucide:move-right" size="18" class="ml-2 transition group-hover:translate-x-1" />
        </nuxt-link>
      </div>
    </div>
  </article>
</template>

<style lang="postcss" scoped></style>
